import { forwardRef } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell, LayoutDashboard, Menu, MessageCircle, Plus, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { LIFECYCLE_BANNER_ID } from '@/shared/hooks/use-lifecycle'

/** The bar's own height above the home-indicator inset: 3.5 rem, iOS and Material's tab-bar height. */
export const PHONE_BAR_HEIGHT = '3.5rem'
/** What a layer that must stop above the bar (the menu, toasts) leaves free at the bottom. */
export const PHONE_BAR_OFFSET = `calc(${PHONE_BAR_HEIGHT} + env(safe-area-inset-bottom, 0px))`

/**
 * The phone's bottom bar — navigation design 3 as Andras amended it (RB-40, D84):
 *
 *   Dashboard · Notifications · + · Chat · Menu
 *
 * The right thumb reaches the bottom-right corner, so Menu lives there; the two things a studio
 * does most on a phone (check what came in, write to the team) and the landing page get a slot
 * each, and "+" opens the create sheet. Everything else is one tap into the full-screen menu.
 *
 * It replaced the top bar AND the two floating buttons. Because the bar sits in the layout rather
 * than over it, nothing has to get out of its way: no scroll-awareness, no composer yield, no
 * bottom padding on <main>. Every slot is ≥ 44 × 44 with its label under the icon — a studio team
 * that is not technical reads words. Below `md` only; the desktop sidebar does not change.
 */
export const PhoneBar = forwardRef<HTMLButtonElement, {
  unreadNotifications: number
  chatUnread: number
  /** On /chat (or a channel): the Chat slot shows as current. */
  onChat: boolean
  onOpenChat: () => void
  notificationsOpen: boolean
  onNotifications: () => void
  onCreate: () => void
  /** The studio is read-only: "+" refuses, as the floating New Task button did (IN-107). */
  createLocked: boolean
  menuOpen: boolean
  onMenu: () => void
}>(function PhoneBar({
  unreadNotifications, chatUnread, onChat, onOpenChat, notificationsOpen, onNotifications,
  onCreate, createLocked, menuOpen, onMenu,
}, menuRef) {
  const { t } = useTranslation()
  const overlayOpen = menuOpen || notificationsOpen
  const slot = 'relative flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 transition-colors active:bg-accent [-webkit-tap-highlight-color:transparent]'
  const label = 'max-w-full truncate text-[11px] leading-tight'

  return (
    <nav
      aria-label={t('layout:mobileNav.title')}
      data-testid="phone-bar"
      className="relative z-50 shrink-0 border-t bg-card pb-[env(safe-area-inset-bottom,0px)] md:hidden"
    >
      <div className="flex items-stretch gap-0.5 px-2" style={{ height: PHONE_BAR_HEIGHT }}>
        <NavLink
          to="/dashboard"
          className={({ isActive }) => cn(slot, isActive && !overlayOpen ? 'text-primary' : 'text-muted-foreground')}
        >
          {({ isActive }) => (
            <>
              <LayoutDashboard size={22} strokeWidth={isActive && !overlayOpen ? 2.5 : 2} />
              <span className={cn(label, isActive && !overlayOpen && 'font-semibold')}>{t('nav.dashboard')}</span>
            </>
          )}
        </NavLink>

        <button
          type="button"
          onClick={onNotifications}
          data-phone-bar-control="notifications"
          aria-expanded={notificationsOpen}
          aria-label={t('layout:notifications.ariaLabel')}
          className={cn(slot, notificationsOpen ? 'text-primary' : 'text-muted-foreground')}
        >
          <span className="relative">
            <Bell size={22} strokeWidth={notificationsOpen ? 2.5 : 2} />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1.5 -right-2.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold leading-none text-white">
                {unreadNotifications > 99 ? '99+' : unreadNotifications}
              </span>
            )}
          </span>
          <span className={cn(label, notificationsOpen && 'font-semibold')} aria-hidden>{t('layout:notifications.title')}</span>
        </button>

        <div className="flex flex-1 items-center justify-center">
          <button
            type="button"
            onClick={onCreate}
            data-phone-bar-control="create"
            disabled={createLocked}
            aria-disabled={createLocked || undefined}
            aria-describedby={createLocked ? LIFECYCLE_BANNER_ID : undefined}
            aria-label={t('common:button.create')}
            title={t('common:button.create')}
            className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform active:scale-95 disabled:pointer-events-none disabled:opacity-40"
          >
            <Plus size={24} />
          </button>
        </div>

        <button
          type="button"
          onClick={onOpenChat}
          aria-current={onChat && !overlayOpen ? 'page' : undefined}
          className={cn(slot, onChat && !overlayOpen ? 'text-primary' : 'text-muted-foreground')}
        >
          <span className="relative">
            <MessageCircle size={22} strokeWidth={onChat && !overlayOpen ? 2.5 : 2} />
            {chatUnread > 0 && (
              <span className="absolute -top-1.5 -right-2.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold leading-none text-primary-foreground">
                {chatUnread > 9 ? '9+' : chatUnread}
              </span>
            )}
          </span>
          <span className={cn(label, onChat && !overlayOpen && 'font-semibold')}>{t('nav.chat')}</span>
        </button>

        <button
          ref={menuRef}
          type="button"
          onClick={onMenu}
          aria-expanded={menuOpen}
          // "Open menu" stays the control's name (it contains the visible "Menu", WCAG 2.5.3) — the
          // name every spec and screen-reader user already knows it by.
          aria-label={t('layout:mobileNav.openMenu')}
          className={cn(slot, menuOpen ? 'text-primary' : 'text-muted-foreground')}
        >
          {menuOpen ? <X size={22} strokeWidth={2.5} /> : <Menu size={22} />}
          <span className={cn(label, menuOpen && 'font-semibold')} aria-hidden>{t('layout:mobileNav.title')}</span>
        </button>
      </div>
    </nav>
  )
})
